import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';

import config from 'financing/config/index.js';
import { ConfigsModel, ContentZonesService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import {
    Account,
    accountActions,
    AccountService,
    AnalyticsService,
    HistoryService,
    ModalService,
    ProfileService,
    VIRTUAL_PAGEVIEWS as PAGES,
    SessionStorageService,
} from '@sm360/widget-commons-general';
import { Translations as FormTranslations } from '@sm360/widget-commons-forms';
import { merge } from 'lodash';

import FinancingForm from 'financing/components/FinancingForm/FinancingForm.jsx';

import configureStore from 'financing/providers/redux.js';

import en from 'financing/i18n/en.json';
import fr from 'financing/i18n/fr.json';

class FinancingProvider extends React.Component {
    constructor(props) {
        super(props);

        merge(en, FormTranslations.en);
        merge(fr, FormTranslations.fr);

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        this.configsModel = new ConfigsModel({
            organizationId: props.organizationId,
            organizationUnitId: props.organizationUnitId,
            organizationUnitLocation: props.organizationUnitLocation,
            websiteId: props.websiteId,
            location: props.location,
        });

        /* TODO: #improvement
         * WARNING their is no contentZones set for checkout. If the contentZones are needed please make sure that
         * Financing in checkout is not broken.
         */
        ContentZonesService.setImageServerBaseUrl(config.source.image);
        ContentZonesService.addContentZones(props.contentZones);

        this.reduxStore = configureStore();
        this.onUnload = this.onUnload.bind(this);
    }

    componentDidMount() {
        this.forceAuthentication();
        SessionStorageService.set('dealerMake', this.props?.dealerMake || '');
        const $window = WindowProxy.get();
        if ($window) {
            $window.addEventListener('beforeunload', this.onUnload);
        }
        AnalyticsService.virtualPageView(PAGES.FINANCING());
    }

    componentWillUnmount() {
        this.onUnload();
    }

    onUnload() {
        HistoryService.add(WindowProxy.location().href);
    }

    onAuthenticationClose() {
        if (AccountService.isAuthenticated()) {
            ProfileService.getLoggedAccountProfile().then((account) => {
                this.reduxStore.dispatch(accountActions.setAccount(new Account(account)));
            });
            return true;
        }
        if (WindowProxy.get()) {
            HistoryService.goBackOrHome();
        }
        return false;
    }

    forceAuthentication() {
        if (!AccountService.isAuthenticated()) {
            const LoginModal = ModalService.get('login/SignUpModal');
            LoginModal.addCallbacks({
                onClose: this.onAuthenticationClose,
            });
            LoginModal.open();
        } else {
            ProfileService.getLoggedAccountProfile().then((account) => {
                this.reduxStore.dispatch(accountActions.setAccount(new Account(account)));
            });
        }
    }

    render() {
        return (
            <Provider store={this.reduxStore}>
                <FinancingForm
                    websiteId={this.configsModel.getWebsiteId()}
                    country={this.configsModel.getCountry()}
                    hasAdobeTagging={this.props.hasAdobeTagging}
                />
            </Provider>
        );
    }
}

FinancingProvider.defaultProps = {
    hasAdobeTagging: false,
};

FinancingProvider.propTypes = {
    locale: PropTypes.string.isRequired,
    websiteId: PropTypes.number.isRequired,
    organizationId: PropTypes.number.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationUnitLocation: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    hasAdobeTagging: PropTypes.bool,
};

FinancingProvider.displayName = 'FinancingProvider';

export default FinancingProvider;
